import {
  computed,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
} from '@angular/core';
import { DropdownComponent } from './dropdown.component';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, fromEvent, switchMap, tap } from 'rxjs';

@Directive({
  selector: '[tvdDropdownVisiblity]',
  host: {
    '(keydown.esc)':
      'dropdown.close(); $event.preventDefault(); $event.stopPropagation();',
  },
})
export class DropdownStateDirective {
  readonly dropdown = inject(DropdownComponent);

  readonly #anchor = computed(
    () => this.dropdown.attachTo()! || this.dropdown.anchor()?.nativeElement,
  );

  readonly #docRef = inject(DOCUMENT);
  readonly #host = inject(ElementRef).nativeElement;
  readonly #destroy = inject(DestroyRef);

  readonly #open$ = toObservable(computed(() => this.dropdown.opened()));

  readonly #anchorClicked = effect(() => {
    if (!this.#anchor()) {
      return;
    }
    fromEvent(this.#anchor(), 'click')
      .pipe(
        takeUntilDestroyed(this.#destroy),
        filter(() => !this.dropdown.disabled()),
        tap((e) => e.preventDefault()),
        tap((e) => e.stopImmediatePropagation()),
      )
      .subscribe(() => !this.dropdown.opened() && this.dropdown.open());
  });

  readonly #stateChanges = this.#open$
    .pipe(
      switchMap(() =>
        // Capturing this event so that anchor's click event's stopPropagation does not affect this event
        fromEvent<MouseEvent>(this.#docRef, 'click', { capture: true }),
      ),
      filter((e) => this.dropdown.opened() && this.#shouldClose(e)),
      takeUntilDestroyed(),
    )
    .subscribe(() => this.dropdown.close());

  #shouldClose(event: MouseEvent) {
    const anchor =
      this.dropdown.attachTo() || this.dropdown.anchor()?.nativeElement;
    return (
      event.target !== anchor &&
      event.target instanceof Node &&
      !this.#host.contains(event.target)
    );
  }
}
